import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Typography,
  Divider,
  Alert,
  Card,
  Row,
  Col,
  message,
  Spin,
} from 'antd';
import {
  MessageOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;

interface SMSSettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

interface SMSConfig {
  account_sid: string;
  auth_token: string;
  phone_number: string;
  enabled: boolean;
}

const SMSSettingsModal: React.FC<SMSSettingsModalProps> = ({
  visible,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null);
  const [currentConfig, setCurrentConfig] = useState<any>(null);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');

  useEffect(() => {
    if (visible) {
      loadCurrentConfig();
    }
  }, [visible]);

  const loadCurrentConfig = async () => {
    try {
      setConfigLoading(true);

      const response = await api.get('/sms/config');
      setCurrentConfig(response.data);

      if (response.data.configured) {
        form.setFieldsValue({
          phone_number: response.data.phone_number,
          enabled: response.data.enabled,
        });
      }
    } catch (error) {
      console.error('Failed to load SMS config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);

      const config: SMSConfig = {
        account_sid: values.account_sid,
        auth_token: values.auth_token,
        phone_number: values.phone_number,
        enabled: values.enabled !== false,
      };

      const response = await api.post('/sms/configure', config);

      if (response.data.success) {
        message.success('SMS settings saved successfully!');
        setConnectionStatus('success');
        loadCurrentConfig();
        form.resetFields(['auth_token']); // Clear sensitive field
      } else {
        message.error(response.data.message || 'Failed to save SMS settings');
        setConnectionStatus('error');
      }
    } catch (error: any) {
      console.error('SMS configuration error:', error);
      message.error(error.response?.data?.message || 'Failed to configure SMS');
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!testPhoneNumber) {
      message.error('Please enter a test phone number');
      return;
    }

    try {
      setTesting(true);

      const response = await api.post('/sms/test-connection', {
        testPhoneNumber,
      });

      if (response.data.success) {
        message.success(
          `Test SMS sent successfully! Cost: $${response.data.cost?.toFixed(4) || '0.0075'}`
        );
        setConnectionStatus('success');
      } else {
        message.error(response.data.message || 'Connection test failed');
        setConnectionStatus('error');
      }
    } catch (error: any) {
      console.error('Connection test error:', error);
      message.error(
        error.response?.data?.message ||
          'Please save your SMS settings first, then test the connection.'
      );
      setConnectionStatus('error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          SMS Settings (Twilio)
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnHidden
      centered
    >
      <Spin spinning={configLoading}>
        {currentConfig?.configured && (
          <Alert
            message="SMS Service Configured"
            description={`Using Twilio number: ${currentConfig.phone_number}`}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 24 }}
          />
        )}

        <Alert
          message="Twilio Account Required"
          description={
            <>
              You need a Twilio account to use SMS notifications. Get started at{' '}
              <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer">
                twilio.com
              </a>
              . SMS costs approximately $0.0075 per message.
            </>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{ enabled: true }}
        >
          <Title level={5}>
            <SettingOutlined /> Twilio Configuration
          </Title>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Account SID"
                name="account_sid"
                rules={[
                  { required: true, message: 'Please enter your Twilio Account SID' },
                  { min: 30, message: 'Account SID should be at least 30 characters' },
                ]}
                tooltip="Found in your Twilio Console dashboard"
              >
                <Input
                  prefix={<SettingOutlined />}
                  placeholder="AC..."
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Auth Token"
                name="auth_token"
                rules={[
                  { required: true, message: 'Please enter your Twilio Auth Token' },
                  { min: 30, message: 'Auth Token should be at least 30 characters' },
                ]}
                tooltip="Found in your Twilio Console dashboard (keep this secret!)"
              >
                <Input.Password
                  prefix={<SettingOutlined />}
                  placeholder="Enter your Twilio Auth Token"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Twilio Phone Number"
                name="phone_number"
                rules={[
                  { required: true, message: 'Please enter your Twilio phone number' },
                  {
                    pattern: /^\+?[1-9]\d{1,14}$/,
                    message: 'Please enter a valid phone number in E.164 format (+15551234567)',
                  },
                ]}
                tooltip="Your Twilio phone number in E.164 format (e.g., +15551234567)"
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="+15551234567"
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Enable SMS Notifications"
                name="enabled"
                valuePropName="checked"
                tooltip="Master switch for SMS notifications. You can also control individual notification types in Notification Settings."
              >
                <Switch
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Title level={5}>
            <PhoneOutlined /> Test Connection
          </Title>

          <Paragraph type="secondary">
            Send a test SMS to verify your Twilio configuration is working correctly.
          </Paragraph>

          <Row gutter={16}>
            <Col span={16}>
              <Input
                prefix={<PhoneOutlined />}
                placeholder="Enter test phone number (e.g., +15551234567)"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
              />
            </Col>
            <Col span={8}>
              <Button
                icon={<MessageOutlined />}
                onClick={handleTestConnection}
                loading={testing}
                block
              >
                Send Test SMS
              </Button>
            </Col>
          </Row>

          {connectionStatus === 'success' && (
            <Alert
              message="Connection Successful!"
              description="Your Twilio configuration is working correctly."
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {connectionStatus === 'error' && (
            <Alert
              message="Connection Failed"
              description="Please check your Twilio credentials and try again."
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Divider />

          <Space style={{ float: 'right' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Configuration
            </Button>
          </Space>
        </Form>

        <Card
          size="small"
          style={{ marginTop: 80, backgroundColor: '#f5f5f5' }}
          title={<Text strong>How to Get Twilio Credentials</Text>}
        >
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Go to <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer">twilio.com</a> and sign up</li>
            <li>Once logged in, find your <strong>Account SID</strong> and <strong>Auth Token</strong> on the dashboard</li>
            <li>Purchase a phone number from Twilio (Phone Numbers → Buy a Number)</li>
            <li>Copy your phone number in E.164 format (e.g., +15551234567)</li>
            <li>Enter the credentials above and click Save</li>
            <li>Configure webhooks in Twilio Console:
              <ul>
                <li><strong>Incoming SMS:</strong> https://your-domain.com/api/sms/webhook/incoming</li>
                <li><strong>Status Callback:</strong> https://your-domain.com/api/sms/webhook/status</li>
              </ul>
            </li>
          </ol>
        </Card>
      </Spin>
    </Modal>
  );
};

export default SMSSettingsModal;
